import classNames from 'classnames';
import { Col, Container, Row } from 'reactstrap';
import InlineSVG from 'svg-inline-react';

import downTriangle from 'public/down-triangle.svg';
import pillHeart from 'public/pill-heart-animated.svg';
import prescriptionSVG from 'public/prescription.svg';
import Button from 'src/app/components/Elements/Button/index';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { DefaultPhoneNumber } from 'src/app/components/Elements/Location';
import { questions } from 'src/app/router/paths';
import { smoothScroll } from 'src/app/utils/smoothScroll';
import { CAPSULE_HELP_EMAIL, IS_IPHONE, IS_MOBILE_SAFARI } from 'src/consts';

import type { PropsFromRedux } from './container';
import styles from './styles.module.scss';

const appStoreUrl = 'https://itunes.apple.com/app/apple-store/id1092333652?pt=117919730&ct=cwebsite&mt=8';

export interface HowItWorksProps extends PropsFromRedux {}

const Renderer = ({ dispatchHowItWorksPageHowCapsuleWorksClick, redirectToOnboarding }: HowItWorksProps) => {
  const handleTransferNowOnClick = () => {
    redirectToOnboarding('How It Works');
  };

  const RenderCta = () => {
    const buttonCSS = 'link-btn link-btn__primary link-btn__medium';
    const desktopLink = (
      <Button onClick={handleTransferNowOnClick} size="medium" className={styles.transferNowButton}>
        Transfer now
      </Button>
    );
    const mobileLink = (
      <a className={buttonCSS} href={appStoreUrl}>
        Download the app
      </a>
    );

    const transferButton = IS_IPHONE ? mobileLink : desktopLink;

    return transferButton;
  };

  return (
    <div id="how-it-works-content" className={styles.page}>
      <main className="sections">
        <section className={classNames(styles.sectionHero, 'relative', 'section--dark', 'p-3', 'width-100')}>
          <div />
          <Container>
            <Row className={styles.heroRow}>
              <Col sm={12} md={10}>
                <h1 className="font-h-3 white center">The pharmacy that comes to you</h1>
                <p className="font-h-1 white center">
                  We deliver your medication whenever you need it. Capsule is always free and your copay will not
                  change.
                </p>
              </Col>
            </Row>
          </Container>
          <a
            className={classNames(styles.sectionHeroNext, { [styles.isMobileSafari]: IS_MOBILE_SAFARI })}
            onClick={e => {
              e.preventDefault();
              smoothScroll('next-scroll');
              dispatchHowItWorksPageHowCapsuleWorksClick();
            }}
            href="#"
            aria-label="Read more about how Capsule works"
          >
            <p
              className={classNames(
                styles.heroNextText,
                'white font-label-callout xs-font-down-3 md-font-down-1 uppercase lg-show pb-3'
              )}
            >
              How Capsule Works
            </p>
            <InlineSVG className={styles.heroArrow} src={downTriangle} />
          </a>
        </section>
        <section className={styles.section} id="next-scroll">
          <Container className="max-width-5 mx-auto">
            <Row>
              <Col sm={12} lg={6}>
                <InlineSVG
                  role="none"
                  focusable="false"
                  aria-hidden="true"
                  raw={true}
                  className={styles.prescriptionGraphic}
                  src={prescriptionSVG}
                />
              </Col>
              <Col sm={12} lg={6}>
                <h2 className="font-h-3 mb-3">Send us your refills or new prescription</h2>
                <h3 className="font-label-callout uppercase font-down-1 green my-3">Send a New Prescription</h3>
                <p className="body">
                  The next time your doctor asks, <strong>&ldquo;What&#8217;s your pharmacy?&rdquo;</strong> say,{' '}
                  <strong>&ldquo;Capsule.&rdquo;</strong>&nbsp; Any doctor can prescribe to Capsule, and you don&#8217;t
                  need an account before they prescribe. We&#8217;ll text you as soon as we receive your prescriptions
                  at the phone number your doctor sent us.
                </p>
                <h3 className="font-label-callout uppercase font-down-1 green my-3">Transfer Your Refills</h3>
                <p className="body">
                  It only takes a minute to transfer refills from your old pharmacy to Capsule. We&#8217;ll contact your
                  old pharmacy and transfer your prescriptions to Capsule within one day so we can deliver your next
                  refill wherever and whenever you need it.
                </p>
              </Col>
            </Row>
          </Container>
        </section>
        <section className={classNames(styles.section, 'section--light')}>
          <div className={styles.pillHeart}>
            <InlineSVG src={pillHeart} />
          </div>
          <Container className="max-width-5 mx-auto clearfix">
            <Row>
              <Col sm={12} lg={6}>
                <h2 className="font-h-3 mb-3">We&#8217;ll be right over dear</h2>
                <p className="body">
                  Once we have your prescription, we’ll text you to schedule your free delivery whenever and wherever
                  you need it. There just needs to be someone – a friend, a doorman, a colleague – to receive and sign
                  for your medication when it arrives.
                </p>
              </Col>
            </Row>
          </Container>
        </section>
        <section className={styles.sectionThree}>
          <div className={classNames(styles.sectionThreeContent, 'max-width-5', 'mx-auto')}>
            <img
              alt="A text message from Capsule, received after going to the doctor, explaining their medication is ready and that there was an automatic discount applied."
              className={styles.sectionThreeImg}
              src={'/public/phone.png'}
            />
            <div className={styles.sectionThreeText}>
              <h2 className="font-h-3 mb-3">Never worry about refills. We keep track for you.</h2>
              <p>
                We coordinate refills with your doctor and copays with your insurance company, so you never have to
                worry about running out of medication again.
              </p>
              <div className={styles.sectionThreeCallout}>
                <h3 className="font-h-1 pl-6 pl-md-0">Capsule is always free</h3>
                <p>
                  We accept all major insurance and your copay will not change. We&#8217;re usually able to look up your
                  insurance information, but if we can&#8217;t find it, we&#8217;ll text you and ask you to send it to
                  us before your first delivery.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className={styles.bottomSection}>
          <Container className="max-width-5 mx-auto">
            <Row>
              <Col xs="12" lg="6" className={classNames(styles.bottomSectionOne, 'p-5', 'p-lg-6')}>
                <h2 className="font-h-3 red">Transfer your prescriptions</h2>
                <p className="p-large mb-3">
                  Tell us which prescriptions you want to switch to Capsule and we&#8217;ll take care of the rest.
                </p>
                <RenderCta />
              </Col>

              <Col xs="12" lg="6" className={classNames(styles.bottomSectionTwo, 'p-5', 'p-lg-6')}>
                <h2 className="font-h-3 red">Need more information? How can we help?</h2>
                <p className="p-large">
                  See more&nbsp;
                  <LinkWithQuery to={questions.toPath()}>questions</LinkWithQuery>
                  <br />
                  Call or text us at <DefaultPhoneNumber />
                  <br />
                  Email <a href={`mailto:${CAPSULE_HELP_EMAIL}`}>{CAPSULE_HELP_EMAIL}</a>
                </p>
                <p className="p-focus mt-4">Did your doctor already send us a prescription?</p>
                <p>
                  We receive your prescription and follow up within an hour, but if you haven&#8217;t heard from us just
                  call or send us a text — <DefaultPhoneNumber />. We&#8217;re always here to help.
                </p>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
    </div>
  );
};

export default Renderer;
